import { useMemo, useState } from "react";

import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import ErrorPage from "@/components/error-page/ErrorPage";
import { Sidebar } from "@/components/sidebar/Sidebar";
import { Topbar } from "@/components/topbar/Topbar";
import { Segmented } from "@/components/ui/Segmented";
import { ToastProvider } from "@/components/ui/Toast";
import {
  TweaksPanel,
  TweakSection,
  TweakSlider,
  TweakToggle,
  TweakRadio,
} from "@/components/tweaks/TweaksPanel";
import { useSidebarStore } from "@/stores/sidebar";
import { useTweaksStore } from "@/stores/tweaks";

interface RouterContext {
  queryClient: QueryClient;
}

/** 페이지 제목 매핑 */
const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "운영 관제 보드",
  "/intake": "접수 목록",
  "/processing": "정보처리 목록",
  "/review": "심사 목록",
  "/evidence": "지급근거 목록",
  "/payment": "지급 · 결제 목록",
  "/post": "사후관리 목록",
  "/admin/aiops": "AI 운영 관리 센터",
  "/admin/control": "내부통제 · 권한",
  "/admin/infra": "인프라 · 성능",
};

const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"] as const;

const PERIOD_OPTIONS = [
  { value: "today", label: "오늘" },
  { value: "week", label: "이번 주" },
  { value: "month", label: "이번 달" },
];

function RootLayout() {
  const pathname = useRouterState({
    select: (s) => s.location.pathname,
  });

  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const role = useTweaksStore((s) => s.role);
  const aiStep = useTweaksStore((s) => s.aiStep);
  const showOnaScenario = useTweaksStore((s) => s.showOnaScenario);
  const setTweak = useTweaksStore((s) => s.setTweak);

  const [dashPeriod, setDashPeriod] = useState("today");

  /* 현재 경로에서 제목 추출 */
  const segments = pathname.split("/").filter(Boolean);
  const basePath = "/" + segments.slice(0, 2).join("/");
  const sectionPath = "/" + (segments[0] ?? "dashboard");
  const isDetail = segments.length >= 2 && !["admin"].includes(segments[0]);
  const isPostDetail =
    isDetail && segments[0] === "post" && segments.length >= 2;

  const DETAIL_TITLES: Record<string, string> = {
    "/intake": "접수 상세",
    "/processing": "정보처리 상세",
    "/review": "심사 상세",
    "/evidence": "지급근거 상세",
    "/payment": "지급 · 결제 상세",
    "/post": "사후관리",
  };

  const title = isDetail
    ? (DETAIL_TITLES[sectionPath] ?? PAGE_TITLES[sectionPath] ?? "Claim-Q")
    : (PAGE_TITLES[basePath] ?? PAGE_TITLES[sectionPath] ?? "Claim-Q");

  const isDashboard = sectionPath === "/dashboard";

  const dashBreadcrumb = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const day = DAY_NAMES[d.getDay()];
    return `${yyyy}.${mm}.${dd} (${day})`;
  }, []);

  return (
    <ToastProvider>
      <div
        className="grid h-screen overflow-hidden transition-[grid-template-columns] duration-200"
        style={{
          gridTemplateColumns: sidebarCollapsed
            ? "4rem 1fr"
            : "var(--sidebar-w) 1fr",
          gridTemplateRows: "1fr",
        }}
      >
        {/* 사이드바 */}
        <Sidebar className="border-r border-border" />

        {/* 메인 영역 */}
        <main className="relative z-10 flex min-w-0 flex-col overflow-x-hidden">
          <Topbar
            title={title}
            breadcrumb={
              isDashboard
                ? dashBreadcrumb
                : isDetail
                  ? isPostDetail
                    ? "#C1047281"
                    : `#${segments[1]}`
                  : undefined
            }
            right={
              isDashboard ? (
                <Segmented
                  options={PERIOD_OPTIONS}
                  value={dashPeriod}
                  onChange={setDashPeriod}
                />
              ) : undefined
            }
          />
          <div className="flex flex-1 flex-col overflow-hidden">
            <OverlayScrollbarsComponent
              className="flex-1"
              options={{ scrollbars: { autoHide: "scroll" } }}
              defer
            >
              <Outlet />
            </OverlayScrollbarsComponent>
            {/* DetailFinalBar가 포탈로 렌더링될 슬롯 */}
            <div id="detail-final-bar-slot" />
          </div>
        </main>
      </div>

      {/* 시연 옵션 패널 */}
      <TweaksPanel title="역할 · 시연 옵션">
        <TweakSection label="역할 전환">
          <TweakRadio
            label="현재 사용자"
            value={role}
            options={[
              { value: "admin", label: "관리자" },
              { value: "reviewer", label: "심사자" },
              { value: "processor", label: "정보처리" },
            ]}
            onChange={(v) =>
              setTweak("role", v as "admin" | "reviewer" | "processor")
            }
          />
        </TweakSection>
        <TweakSection label="시연 옵션">
          <TweakSlider
            label="AI 산출 4-Step 진행도"
            value={aiStep}
            min={0}
            max={4}
            step={1}
            onChange={(v) => setTweak("aiStep", v)}
          />
          <TweakToggle
            label="오나영 시나리오 자동 채움"
            value={showOnaScenario}
            onChange={(v) => setTweak("showOnaScenario", v)}
          />
        </TweakSection>
      </TweaksPanel>
    </ToastProvider>
  );
}

function RouteErrorComponent() {
  return <ErrorPage type="error" />;
}

function NotFoundComponent() {
  return <ErrorPage type="notFound" />;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RouteErrorComponent,
  notFoundComponent: NotFoundComponent,
});
